'use client';

import { useEffect } from 'react';

import { isServer } from '@/app/utils/browser';
import { useMediaQuery } from '@/components/hooks/useMediaQuery';

export interface RevealAnimOpts {
  groupOnce?: boolean;
  itemOnce?: boolean;
  groupMargin?: string;
  itemMargin?: string;
  groupAmount?: number;
  itemAmount?: number;
  defaultStagger?: number;
  defaultBaseDelay?: number;
  enabled?: boolean;
}

/**
 * - Group container: [data-revealanim-group] (+ optional: data-stagger, data-delay-base)
 * - Child item:      [data-revealanim] (+ optional: data-order)
 * - Standalone item: [data-revealanim] (+ optional: data-delay)
 */
export const useRevealAnim = (opts?: RevealAnimOpts) => {
  const {
    groupOnce = true,
    itemOnce = true,
    groupMargin = '0px 0px 0px 0px',
    itemMargin = '0px 0px 0px 0px',
    groupAmount = 0.25,
    itemAmount = 0.25,
    defaultStagger = 0.08,
    defaultBaseDelay = 0,
    enabled = true,
  } = opts ?? {};

  const isReducedMotion =
    useMediaQuery('(prefers-reduced-motion: reduce)') ?? false;

  useEffect(() => {
    if (!enabled || isServer) return;

    // ---------- GROUP MODE ----------
    const groups = Array.from(
      document.querySelectorAll<HTMLElement>('[data-revealanim-group]')
    );

    const groupIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const group = entry.target as HTMLElement;
          const visible =
            entry.isIntersecting || entry.intersectionRatio >= groupAmount;

          if (visible) {
            const stagger =
              Number(group.dataset.stagger ?? defaultStagger) || 0;
            const base =
              Number(group.dataset.delayBase ?? defaultBaseDelay) || 0;

            group.style.setProperty('--stagger', `${stagger}s`);
            group.style.setProperty('--delay-base', `${base}s`);
            group.setAttribute('data-revealanim-group-visible', '1');

            // children
            const items = Array.from(
              group.querySelectorAll<HTMLElement>('[data-revealanim]')
            );
            items.forEach((el, idx) => {
              const order = Number(el.dataset.order ?? idx);
              el.style.setProperty('--i', String(order));
              if (!isReducedMotion) el.style.removeProperty('transitionDelay');
              el.setAttribute('data-revealanim-visible', '1');
            });

            if (groupOnce) groupIO.unobserve(group);
          } else if (!groupOnce) {
            group.removeAttribute('data-revealanim-group-visible');
            const items =
              group.querySelectorAll<HTMLElement>('[data-revealanim]');
            items.forEach((el) => {
              el.removeAttribute('data-revealanim-visible');
              el.style.removeProperty('--i');
              el.style.removeProperty('transitionDelay');
            });
          }
        });
      },
      { root: null, rootMargin: groupMargin, threshold: groupAmount }
    );

    groups.forEach((g) => groupIO.observe(g));

    // ---------- STANDALONE MODE ----------
    const allRevealElements = Array.from(
      document.querySelectorAll<HTMLElement>('[data-revealanim]')
    );
    const standalone = allRevealElements.filter(
      (el) => !el.closest('[data-revealanim-group]')
    );

    const itemIO = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          const visible =
            entry.isIntersecting || entry.intersectionRatio >= itemAmount;

          if (visible) {
            const delay = Number(el.dataset.delay ?? 0) || 0;
            if (!isReducedMotion) el.style.transitionDelay = `${delay}s`;
            el.setAttribute('data-revealanim-visible', '1');
            if (itemOnce) itemIO.unobserve(el);
          } else if (!itemOnce) {
            el.removeAttribute('data-revealanim-visible');
            el.style.removeProperty('transitionDelay');
          }
        });
      },
      { root: null, rootMargin: itemMargin, threshold: itemAmount }
    );

    standalone.forEach((el) => itemIO.observe(el));

    return () => {
      groupIO.disconnect();
      itemIO.disconnect();
    };
  }, [
    enabled,
    groupOnce,
    itemOnce,
    groupMargin,
    itemMargin,
    groupAmount,
    itemAmount,
    defaultStagger,
    defaultBaseDelay,
    isReducedMotion,
  ]);
};
